import { Component, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { combineLatest, filter, Observable, pairwise, startWith } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { ConversationListService } from '../../../../../../core/src/lib/state/conversation-list.service';
import { ConversationService } from '../../../../../../core/src/lib/state/conversation.service';
import { InboxService } from '../../../../../../core/src/lib/inbox.service';
import { ConversationDetail } from '../../../../../../core/src/lib/interface/conversation.interface';
import { toFirestoreId } from '../../../../../../core/src/lib/conversation-utils';
import { AiAssistantConversationService } from '../../../../../../core/src/lib/state/ai-assistant-conversation.service';
import { ACCOUNT_GROUP_ID_TOKEN, PARTNER_ID_TOKEN, USER_ID_TOKEN } from '../../../../../../core/src/lib/tokens';
import { CommonModule } from '@angular/common';
import { ConversationPipesModule } from '../../../../../../pipes';
import { ConversationUIModule } from '../../../../conversation-ui.module';
import { GalaxyInfiniteScrollTriggerModule } from '@vendasta/galaxy/infinite-scroll-trigger';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { InboxViewsEmptyComponent } from '../../../../components/inbox-views-empty/inbox-views-empty.component';
import { groupConversations, GroupedConversations } from './util';
import { filterNullAndUndefined } from '@vendasta/rx-utils';
import { InboxNavigationService } from '../../../../../../core/src/lib/inbox-navigation.service';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'inbox-grouped-conversation-list',
  imports: [
    CommonModule,
    ConversationPipesModule,
    ConversationUIModule,
    GalaxyInfiniteScrollTriggerModule,
    GalaxyLoadingSpinnerModule,
    ReactiveFormsModule,
    TranslateModule,
    MatButton,
    MatIcon,
    InboxViewsEmptyComponent,
  ],
  templateUrl: './inbox-grouped-conversation-list.component.html',
  styleUrl: './inbox-grouped-conversation-list.component.scss',
})
export class InboxGroupedConversationListComponent {
  rootChatPath = input<string | undefined>(undefined);

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly activeRoute = inject(ActivatedRoute);
  private readonly inboxService = inject(InboxService);
  private readonly conversationService = inject(ConversationService);
  private readonly conversationListService = inject(ConversationListService);
  private readonly aiConversationService = inject(AiAssistantConversationService);
  private readonly inboxNavigationService = inject(InboxNavigationService);

  protected readonly userId$ = inject(USER_ID_TOKEN);
  protected readonly partnerId$ = inject(PARTNER_ID_TOKEN);
  protected readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);
  protected readonly showInboxViews$ = this.inboxService.showInboxViews$;

  readonly loadingConversations$ = this.conversationListService.loadingConversations$;
  readonly emptyConversations$ = this.conversationListService.emptyConversations$;
  readonly showInboxZero$ = combineLatest([this.emptyConversations$, this.loadingConversations$]).pipe(
    map(([empty, loading]) => empty && !loading),
  );

  readonly groupedConversations$: Observable<GroupedConversations[]>;

  private readonly aiAssistantId$ = this.aiConversationService.aiAssistantId$;
  readonly aiAssistantId = toSignal(this.aiAssistantId$);

  readonly isOverlay = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.route?.snapshot?.data?.['isOverlay'] ?? false),
      startWith(this.route?.snapshot?.data?.['isOverlay'] ?? false),
    ),
    { initialValue: false },
  );

  readonly currentConversationId = toSignal(this.conversationService.currentFirestoreConversationId$, {
    initialValue: '',
  });

  readonly isCreatingConversation = signal(false);

  constructor() {
    // Select the most recent conversation when conversations first load
    combineLatest([
      this.conversationListService.conversations$.pipe(filterNullAndUndefined()),
      this.conversationService.currentFirestoreConversationId$,
      this.conversationListService.loadingConversations$.pipe(pairwise()),
    ])
      .pipe(
        takeUntilDestroyed(),
        filter(([conversations, id, [prevLoading, loading]]): boolean => {
          if (!prevLoading || loading) {
            return false;
          }
          return Boolean(id === '' && conversations && conversations.length > 0);
        }),
        map(([conversations]) => {
          return toFirestoreId(conversations[0].conversation.conversationId);
        }),
      )
      .subscribe((id) => {
        this.conversationService.setCurrentFirestoreConversationId(id);
      });

    this.groupedConversations$ = this.conversationListService.conversations$.pipe(
      map((conversations: ConversationDetail[] | null): GroupedConversations[] => {
        return groupConversations(conversations || []);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.activeRoute.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (queryParams) => {
        this.conversationListService.updateFilters({
          isPrivate: true,
          isOpen: queryParams.get('view') !== 'closed',
          isAnonymous: false,
        });
      },
    });
  }

  getConversationPath(id?: string): string | undefined {
    if (!id) {
      return this.rootChatPath();
    }
    return this.rootChatPath() + `/${toFirestoreId(id)}`;
  }

  conversationSelected(conversationId?: string): void {
    if (!conversationId) {
      return;
    }

    const path = this.getConversationPath(conversationId);
    if (!path) {
      return;
    }

    this.inboxNavigationService.navigateToConversation(path, this.isOverlay());
    this.conversationService.conversationSelected(true);
  }

  trackConversationDetailsById(conversationDetail: ConversationDetail): string | undefined {
    return conversationDetail?.conversation?.conversationId;
  }

  isConversationActive(conversationId?: string): boolean {
    if (!conversationId) {
      return false;
    }
    return toFirestoreId(conversationId) === this.currentConversationId();
  }

  loadMoreConversations(): void {
    this.conversationListService.loadMoreConversations();
  }

  async createConversation() {
    if (this.isCreatingConversation()) {
      return;
    }

    this.isCreatingConversation.set(true);
    try {
      await this.aiConversationService.startConversation();
    } finally {
      this.isCreatingConversation.set(false);
    }
  }
}
